import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  Button,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Collapse,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { TrendingUp, TrendingDown, Refresh, NewspaperOutlined } from '@mui/icons-material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { apiService } from '../services/api';
import { useTheme } from '@mui/material/styles';

const MediaTrending: React.FC = () => {
  const theme = useTheme();
  const [trendingData, setTrendingData] = useState<any | null>(null);
  const [trendingLoading, setTrendingLoading] = useState(false);
  const [trendingError, setTrendingError] = useState<string | null>(null);
  const [scrapingInProgress, setScrapingInProgress] = useState(false);
  const [expandedSymbol, setExpandedSymbol] = useState<string | null>(null);
  const [scrapeStatus, setScrapeStatus] = useState<string | null>(null);
  const [availableMonths, setAvailableMonths] = useState<string[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>('latest');

  const fetchTrending = useCallback(async (month: string = 'latest') => {
    setTrendingLoading(true);
    setTrendingError(null);
    try {
      const data = month === 'latest'
        ? await apiService.getLatestTrendingStocks(1)
        : await apiService.getTrendingStocksForMonth(month);
      setTrendingData(data);
    } catch (err: any) {
      setTrendingError(err.response?.data?.detail || 'Failed to fetch trending stocks');
    } finally {
      setTrendingLoading(false);
    }
  }, []);

  const fetchMonths = useCallback(async () => {
    try {
      const result = await apiService.getTrendingMonths();
      setAvailableMonths(result.months || []);
    } catch {
      // silently ignore — month picker just won't appear
    }
  }, []);

  useEffect(() => {
    fetchMonths();
    fetchTrending('latest');
  }, [fetchTrending, fetchMonths]);

  const handleTriggerScrape = async () => {
    setScrapingInProgress(true);
    setScrapeStatus(null);
    try {
      const resp = await apiService.triggerTrendingScrape();
      setScrapeStatus(resp.message || 'Scrape started. Check back in ~10 minutes.');
    } catch (err: any) {
      setScrapeStatus('Failed to trigger scrape: ' + (err.response?.data?.detail || err.message));
    } finally {
      setScrapingInProgress(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NewspaperOutlined />
          <Typography variant="h4" fontWeight={700}>
            Media Trending Stocks
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {availableMonths.length > 1 && (
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Month</InputLabel>
              <Select
                value={selectedMonth}
                label="Month"
                onChange={(e) => {
                  const m = e.target.value as string;
                  setSelectedMonth(m);
                  fetchTrending(m);
                }}
              >
                <MenuItem value="latest">Latest</MenuItem>
                {availableMonths.map((m) => (
                  <MenuItem key={m} value={m}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={trendingLoading ? <CircularProgress size={14} /> : <Refresh />}
            onClick={() => fetchTrending(selectedMonth)}
            disabled={trendingLoading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={scrapingInProgress ? <CircularProgress size={14} color="inherit" /> : <NewspaperOutlined />}
            onClick={handleTriggerScrape}
            disabled={scrapingInProgress}
          >
            {scrapingInProgress ? 'Starting…' : 'Scrape Now'}
          </Button>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Stocks mentioned in Motley Fool, Yahoo Finance, Kiplinger, Barron's, IBD, MarketBeat, Morningstar.
        Updated monthly. Enriched with financials, performance, and competitor comparison.
      </Typography>
      {trendingData?.month && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          Data for: <strong>{trendingData.month}</strong> · {trendingData.count} stocks tracked
        </Typography>
      )}
      {scrapeStatus && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setScrapeStatus(null)}>
          {scrapeStatus}
        </Alert>
      )}

      {trendingLoading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 3 }} />}
      {trendingError && <Alert severity="error" sx={{ mb: 2 }}>{trendingError}</Alert>}

      {!trendingLoading && trendingData && (
        trendingData.count === 0 ? (
          <Alert severity="info">
            No data yet for this month. Click "Scrape Now" to fetch trending stocks from financial media.
            The first run takes ~10-15 minutes.
          </Alert>
        ) : (
          <Card>
            <TableContainer sx={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f5f5f5' } }}>
                    <TableCell sx={{ width: 32 }} />
                    <TableCell><strong>Symbol</strong></TableCell>
                    <TableCell><strong>First Seen</strong></TableCell>
                    <TableCell><strong>Sources</strong></TableCell>
                    <TableCell align="right"><strong>Revenue</strong></TableCell>
                    <TableCell align="right"><strong>Rev. Growth</strong></TableCell>
                    <TableCell align="right"><strong>P/S</strong></TableCell>
                    <TableCell align="right"><strong>1Y Change</strong></TableCell>
                    <TableCell><strong>Sector / Industry</strong></TableCell>
                    <TableCell align="center"><strong>Profitable</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trendingData.stocks.map((stock: any) => {
                    const isExpanded = expandedSymbol === stock.symbol;
                    const change1y = stock.price_change_1y_pct;
                    const changeColor =
                      change1y === null ? 'text.secondary'
                      : change1y > 20 ? 'success.main'
                      : change1y > 10 ? '#8bc34a'
                      : change1y < -20 ? 'error.main'
                      : change1y < -10 ? 'warning.main'
                      : 'text.secondary';
                    const revGrowth = stock.revenue_growth_yoy;

                    return (
                      <React.Fragment key={stock.symbol}>
                        <TableRow
                          hover
                          sx={{ cursor: 'pointer', '& > *': { borderBottom: isExpanded ? 0 : undefined } }}
                          onClick={() => setExpandedSymbol(isExpanded ? null : stock.symbol)}
                        >
                          <TableCell>
                            <IconButton size="small">
                              {isExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Tooltip
                              title={stock.company_name
                                ? `${stock.company_name}${stock.sector ? ` · ${stock.sector}` : ''}${stock.industry ? ` · ${stock.industry}` : ''}`
                                : stock.symbol}
                              placement="right"
                              arrow
                            >
                              <Box sx={{ cursor: 'default', display: 'inline-block' }}>
                                <Typography variant="body2" fontWeight={700}>
                                  {stock.symbol}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ maxWidth: 140 }}>
                                  {stock.company_name}
                                </Typography>
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                              {stock.earliest_pub_date || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                              {(stock.sources || []).map((src: string) => {
                                const srcArticles = (stock.articles || []).filter((a: any) => a.source === src);
                                const latestArticle = srcArticles.sort((a: any, b: any) =>
                                  (a.pub_date || '').localeCompare(b.pub_date || '')
                                )[0];
                                const label = src.replace('.com', '').replace('finance.yahoo', 'yahoo');
                                const dateStr = latestArticle?.pub_date ? ` ${latestArticle.pub_date.slice(5)}` : '';
                                return latestArticle?.url ? (
                                  <Tooltip key={src} title={latestArticle.title || src} placement="top" arrow>
                                    <Chip
                                      component="a"
                                      href={latestArticle.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      label={`${label}${dateStr}`}
                                      size="small"
                                      clickable
                                      sx={{ fontSize: '0.65rem', height: 18 }}
                                    />
                                  </Tooltip>
                                ) : (
                                  <Chip
                                    key={src}
                                    label={`${label}${dateStr}`}
                                    size="small"
                                    sx={{ fontSize: '0.65rem', height: 18 }}
                                  />
                                );
                              })}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {stock.revenue_ttm_m != null
                                ? stock.revenue_ttm_m >= 1000
                                  ? `$${(stock.revenue_ttm_m / 1000).toFixed(1)}B`
                                  : `$${stock.revenue_ttm_m.toFixed(0)}M`
                                : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {revGrowth != null ? (
                              <Chip
                                label={`${revGrowth >= 0 ? '+' : ''}${(revGrowth * 100).toFixed(1)}%`}
                                size="small"
                                color={revGrowth > 0.1 ? 'success' : revGrowth < 0 ? 'error' : 'default'}
                                icon={revGrowth > 0 ? <TrendingUp style={{ fontSize: 12 }} /> : <TrendingDown style={{ fontSize: 12 }} />}
                              />
                            ) : <Typography variant="body2" color="text.secondary">—</Typography>}
                          </TableCell>
                          <TableCell align="right">
                            {stock.ps_ratio != null ? (
                              <Tooltip title="Price-to-Sales ratio. Lower = cheaper relative to revenue.">
                                <Typography
                                  variant="body2"
                                  sx={{ color: stock.ps_ratio < 5 ? 'success.main' : stock.ps_ratio > 20 ? 'error.main' : 'text.primary' }}
                                >
                                  {stock.ps_ratio.toFixed(1)}x
                                </Typography>
                              </Tooltip>
                            ) : <Typography variant="body2" color="text.secondary">—</Typography>}
                          </TableCell>
                          <TableCell align="right">
                            {change1y != null ? (
                              <Typography variant="body2" fontWeight={600} sx={{ color: changeColor }}>
                                {change1y >= 0 ? '+' : ''}{change1y.toFixed(1)}%
                              </Typography>
                            ) : <Typography variant="body2" color="text.secondary">—</Typography>}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" display="block" color="text.secondary">
                              {stock.sector || '—'}
                            </Typography>
                            <Typography variant="caption" display="block" noWrap sx={{ maxWidth: 160 }}>
                              {stock.industry || ''}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {stock.is_profitable === null ? (
                              <Typography variant="body2" color="text.secondary">—</Typography>
                            ) : (
                              <Chip
                                label={stock.is_profitable ? 'Yes' : 'No'}
                                size="small"
                                color={stock.is_profitable ? 'success' : 'error'}
                                variant="outlined"
                              />
                            )}
                          </TableCell>
                        </TableRow>

                        {/* Expanded: detailed analysis + competitors */}
                        <TableRow>
                          <TableCell colSpan={10} sx={{ py: 0, bgcolor: theme.palette.mode === 'dark' ? '#111' : '#fafafa' }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 2 }}>
                                <Grid container spacing={3}>
                                  {/* Left: Financials & Performance */}
                                  <Grid item xs={12} md={4}>
                                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                      Financials & Performance
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" color="text.secondary">Market Cap</Typography>
                                        <Typography variant="caption" fontWeight={600}>
                                          {stock.market_cap_b != null ? `$${stock.market_cap_b.toFixed(1)}B` : '—'}
                                        </Typography>
                                      </Box>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" color="text.secondary">EPS (TTM)</Typography>
                                        <Typography variant="caption" fontWeight={600} sx={{ color: (stock.eps_ttm || 0) > 0 ? 'success.main' : 'error.main' }}>
                                          {stock.eps_ttm != null ? `$${stock.eps_ttm.toFixed(2)}` : '—'}
                                        </Typography>
                                      </Box>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" color="text.secondary">P/E Ratio</Typography>
                                        <Typography variant="caption" fontWeight={600}>
                                          {stock.pe_ratio != null ? stock.pe_ratio.toFixed(1) : '—'}
                                        </Typography>
                                      </Box>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" color="text.secondary">Current Price</Typography>
                                        <Typography variant="caption" fontWeight={600}>
                                          {stock.price_current != null ? `$${stock.price_current.toFixed(2)}` : '—'}
                                        </Typography>
                                      </Box>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" color="text.secondary">52W High</Typography>
                                        <Typography variant="caption">
                                          {stock.price_52w_high != null ? `$${stock.price_52w_high.toFixed(2)}` : '—'}
                                          {stock.price_52w_high_pct != null && (
                                            <span style={{ color: '#f44336', marginLeft: 4 }}>
                                              ({stock.price_52w_high_pct.toFixed(1)}% away)
                                            </span>
                                          )}
                                        </Typography>
                                      </Box>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" color="text.secondary">52W Low</Typography>
                                        <Typography variant="caption">
                                          {stock.price_52w_low != null ? `$${stock.price_52w_low.toFixed(2)}` : '—'}
                                          {stock.price_52w_low_pct != null && (
                                            <span style={{ color: '#4caf50', marginLeft: 4 }}>
                                              (+{stock.price_52w_low_pct.toFixed(1)}% above)
                                            </span>
                                          )}
                                        </Typography>
                                      </Box>
                                    </Box>
                                    {(stock.articles || []).length > 0 && (
                                      <Box sx={{ mt: 1.5 }}>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, fontWeight: 600 }}>
                                          Articles:
                                        </Typography>
                                        {stock.articles.map((art: any, i: number) => (
                                          <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.3 }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', minWidth: 60 }}>
                                              {art.pub_date ? art.pub_date.slice(5) : '—'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                              <strong>{art.source?.replace('.com','').replace('finance.yahoo','yahoo')}</strong>
                                              {' · '}
                                              {art.url
                                                ? <a href={art.url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                                                    {art.title?.substring(0, 90)}{(art.title?.length || 0) > 90 ? '…' : ''}
                                                  </a>
                                                : art.title?.substring(0, 90)
                                              }
                                            </Typography>
                                          </Box>
                                        ))}
                                      </Box>
                                    )}
                                  </Grid>

                                  {/* Right: Competitors */}
                                  <Grid item xs={12} md={8}>
                                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                      Competitors — {stock.industry || stock.sector || 'Same Sector'}
                                    </Typography>
                                    {(stock.competitors || []).length === 0 ? (
                                      <Typography variant="caption" color="text.secondary">No competitor data</Typography>
                                    ) : (
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow>
                                            <TableCell sx={{ py: 0.5 }}><Typography variant="caption" fontWeight={600}>Symbol</Typography></TableCell>
                                            <TableCell align="right" sx={{ py: 0.5 }}><Typography variant="caption" fontWeight={600}>Mkt Cap</Typography></TableCell>
                                            <TableCell align="right" sx={{ py: 0.5 }}><Typography variant="caption" fontWeight={600}>Revenue</Typography></TableCell>
                                            <TableCell align="right" sx={{ py: 0.5 }}><Typography variant="caption" fontWeight={600}>Rev Growth</Typography></TableCell>
                                            <TableCell align="right" sx={{ py: 0.5 }}><Typography variant="caption" fontWeight={600}>P/S</Typography></TableCell>
                                            <TableCell align="right" sx={{ py: 0.5 }}><Typography variant="caption" fontWeight={600}>1Y Chg</Typography></TableCell>
                                            <TableCell align="center" sx={{ py: 0.5 }}><Typography variant="caption" fontWeight={600}>Profitable</Typography></TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {stock.competitors.map((comp: any) => (
                                            <TableRow key={comp.symbol} hover>
                                              <TableCell sx={{ py: 0.5 }}>
                                                <Typography variant="caption" fontWeight={700}>{comp.symbol}</Typography>
                                                <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ maxWidth: 120 }}>{comp.name}</Typography>
                                              </TableCell>
                                              <TableCell align="right" sx={{ py: 0.5 }}>
                                                <Typography variant="caption">
                                                  {comp.market_cap_b != null ? `$${comp.market_cap_b.toFixed(0)}B` : '—'}
                                                </Typography>
                                              </TableCell>
                                              <TableCell align="right" sx={{ py: 0.5 }}>
                                                <Typography variant="caption">
                                                  {comp.revenue_m != null
                                                    ? comp.revenue_m >= 1000
                                                      ? `$${(comp.revenue_m / 1000).toFixed(1)}B`
                                                      : `$${comp.revenue_m.toFixed(0)}M`
                                                    : '—'}
                                                </Typography>
                                              </TableCell>
                                              <TableCell align="right" sx={{ py: 0.5 }}>
                                                {comp.revenue_growth != null ? (
                                                  <Typography variant="caption" sx={{ color: comp.revenue_growth > 0 ? 'success.main' : 'error.main' }}>
                                                    {comp.revenue_growth >= 0 ? '+' : ''}{(comp.revenue_growth * 100).toFixed(1)}%
                                                  </Typography>
                                                ) : <Typography variant="caption" color="text.secondary">—</Typography>}
                                              </TableCell>
                                              <TableCell align="right" sx={{ py: 0.5 }}>
                                                {comp.ps_ratio != null ? (
                                                  <Typography variant="caption" sx={{ color: comp.ps_ratio < 5 ? 'success.main' : comp.ps_ratio > 20 ? 'error.main' : 'text.primary' }}>
                                                    {comp.ps_ratio.toFixed(1)}x
                                                  </Typography>
                                                ) : <Typography variant="caption" color="text.secondary">—</Typography>}
                                              </TableCell>
                                              <TableCell align="right" sx={{ py: 0.5 }}>
                                                {comp.price_change_1y_pct != null ? (
                                                  <Typography variant="caption" fontWeight={600} sx={{ color: comp.price_change_1y_pct > 20 ? 'success.main' : comp.price_change_1y_pct < -20 ? 'error.main' : 'text.primary' }}>
                                                    {comp.price_change_1y_pct >= 0 ? '+' : ''}{comp.price_change_1y_pct.toFixed(1)}%
                                                  </Typography>
                                                ) : <Typography variant="caption" color="text.secondary">—</Typography>}
                                              </TableCell>
                                              <TableCell align="center" sx={{ py: 0.5 }}>
                                                {comp.is_profitable == null ? '—' : (
                                                  <Typography variant="caption" sx={{ color: comp.is_profitable ? 'success.main' : 'error.main' }}>
                                                    {comp.is_profitable ? '✓' : '✗'}
                                                  </Typography>
                                                )}
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    )}
                                  </Grid>
                                </Grid>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )
      )}
    </Box>
  );
};

export default MediaTrending;
